import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { jsonFromArray, PUBLIC_TUTORIALS_HUB_KEY } from "../../utility";
import UserAvatar from "../management/UserAvatar";
import ShareDialogGroupHeader, {
    buildGroupCoverage,
    resolveGroupMembers,
    ShareDialogAvailableGroupRow,
    ShareDialogGroupIcon,
    ShareDialogGroupMembersDialog,
    ShareDialogGroupUserRow,
    ShareDialogPanelHeading,
    ShareDialogUserListLoading,
    ShareDialogUserSectionHeader,
    shareDialogSearchSx,
} from "../ShareDialogGroupHeader";
import ShareDialogDeliveryOptions from "../ShareDialogDeliveryOptions";
import ShareDialogDeliverySummary from "../ShareDialogDeliverySummary";
import ShareDialogSilentShareConfirm from "../ShareDialogSilentShareConfirm";
import useShareDialogUsersTabLoading from "../useShareDialogUsersTabLoading";
import {
    buildShareFeedbackMessage,
    buildSharedItemEmailHtml,
    sendSharedItemEmails,
} from "../shareDialogDelivery";

const surfaceSx = {
    borderRadius: 2.5,
    borderColor: "divider",
    overflow: "hidden",
};

function PermissionSwitch({ checked, label, onChange }) {
    return (
        <Box
            component="label"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                color: "text.secondary",
                whiteSpace: "nowrap",
            }}
        >
            <Switch
                size="small"
                checked={checked}
                onChange={onChange}
                inputProps={{ "aria-label": label }}
            />
            <Typography variant="caption">Can collaborate</Typography>
        </Box>
    );
}

function AccessRow({
    avatar,
    canEdit,
    onChangePermission,
    onRemove,
    onViewMembers,
    secondary,
    title,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 1.25, md: 2 },
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
            }}
        >
            <Box
                // Only the name side of the row opens the member list, so the
                // permission switch keeps its own click target
                onClick={onViewMembers}
                onKeyDown={
                    onViewMembers
                        ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  onViewMembers();
                              }
                          }
                        : undefined
                }
                role={onViewMembers ? "button" : undefined}
                tabIndex={onViewMembers ? 0 : undefined}
                aria-label={
                    onViewMembers ? `View the members of ${title}` : undefined
                }
                sx={{
                    width: { xs: "100%", md: "auto" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flex: 1,
                    minWidth: 0,
                    ...(onViewMembers
                        ? {
                              cursor: "pointer",
                              borderRadius: 1.5,
                              "&:hover": {
                                  backgroundColor: "action.hover",
                              },
                          }
                        : {}),
                }}
            >
                {avatar}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 800 }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {secondary}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    width: { xs: "100%", md: "auto" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "space-between", md: "flex-end" },
                    gap: 0.5,
                    pl: { xs: 5.75, md: 0 },
                }}
            >
                <PermissionSwitch
                    checked={canEdit}
                    label={`${title}: Can collaborate`}
                    onChange={onChangePermission}
                />
                <IconButton
                    size="small"
                    aria-label={`Remove ${title}`}
                    onClick={onRemove}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}

export default function ShareProjectDialog(props) {
    const { enqueueSnackbar } = useSnackbar();
    const {
        users = [],
        groups = [],
        folders = [],
        foldersLoaded = false,
        groupsLoaded = false,
    } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [availableHubs, setAvailableHubs] = useState([]);
    const [usernameFilter, setUsernameFilter] = useState("");
    const [selectedUserFilter, setSelectedUserFilter] = useState("");
    const [selectedGroupFilter, setSelectedGroupFilter] = useState("");
    const [availableGroupFilter, setAvailableGroupFilter] = useState("");
    const [activeTab, setActiveTab] = useState(0);
    // The { group, hub } whose members are being viewed, or null
    const [viewingGroup, setViewingGroup] = useState(null);
    const { beginUsersTabLoading, usersTabLoading } =
        useShareDialogUsersTabLoading(activeTab);
    // In-platform notifications are the expected outcome of sharing, so they
    // start on and have to be deliberately turned off.
    const [sendNotifications, setSendNotifications] = useState(true);
    const [sendEmails, setSendEmails] = useState(false);
    const [embargoNotifications, setEmbargoNotifications] = useState(false);
    const messageRef = useRef("");
    const deliveryOptionsRef = useRef(null);
    const [fromDate, setFromDate] = useState(new Date());
    const [silentShareConfirmOpen, setSilentShareConfirmOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const isLoading = !currentUserData || !foldersLoaded || !groupsLoaded;

    useEffect(() => {
        if (!props.open || !props.projectInfo || isLoading) {
            return;
        }

        const userCanBeSeen = (user) => {
            if (user.key === currentUserData.key) {
                return false;
            }

            return (
                (user.admins || []).includes(currentUserData.key) ||
                user.createdBy === currentUserData.key ||
                currentUserData.role === "System Digital Manager"
            );
        };

        const visibleUsers = users.filter(userCanBeSeen);
        const projectUsers = props.projectInfo.users || [];
        setAvailableUsers(visibleUsers);
        setSelectedUsers(
            visibleUsers
                .filter((user) =>
                    projectUsers.some((item) => item.key === user.key)
                )
                .map((user) => {
                    const access = projectUsers.find(
                        (item) => item.key === user.key
                    );
                    return { ...user, canEdit: Boolean(access?.canEdit) };
                })
        );

        const projectGroups = props.projectInfo.groups || [];
        const hubs = folders
            .filter(
                (folder) =>
                    folder.hub &&
                    ((folder.managers || []).includes(currentUserData.key) ||
                        (folder.users || []).includes(currentUserData.key))
            )
            .map((hub) => ({
                ...hub,
                groups: groups
                    .filter((group) => group.hub === hub.key)
                    .map((group) => ({ ...group, hubName: hub.name })),
            }));

        setAvailableHubs(hubs);
        setSelectedGroups(
            hubs
                .flatMap((hub) => hub.groups)
                .filter((group) =>
                    projectGroups.some((item) => item.key === group.key)
                )
                .map((group) => {
                    const access = projectGroups.find(
                        (item) => item.key === group.key
                    );
                    return { ...group, canEdit: Boolean(access?.canEdit) };
                })
        );
        setUsernameFilter("");
        setSelectedUserFilter("");
        setSelectedGroupFilter("");
        setAvailableGroupFilter("");
        setActiveTab(0);
        setViewingGroup(null);
        setSendNotifications(true);
        setSendEmails(false);
        setEmbargoNotifications(false);
        setSilentShareConfirmOpen(false);
        messageRef.current = "";
        setFromDate(new Date());
        setSaveError("");
        setIsSaving(false);
    }, [
        currentUserData,
        folders,
        groups,
        isLoading,
        props.open,
        props.projectInfo,
        users,
    ]);

    const groupCoverage = useMemo(
        () =>
            buildGroupCoverage({
                getGroupName: (group) => group.displayName || group.name,
                selectedGroups,
                users,
            }),
        [selectedGroups, users]
    );
    const selectedGroupUserKeys = groupCoverage.memberKeys;

    const viewedGroupMembers = resolveGroupMembers(viewingGroup?.group, users);

    const handleViewGroupMembers = (group, hub) => () => {
        setViewingGroup({ group, hub });
    };

    const handleGroupMembersClose = () => {
        setViewingGroup(null);
    };

    const directlySelectedUsers = selectedUsers.filter(
        (user) => !selectedGroupUserKeys.has(user.key)
    );

    const recipients = useMemo(() => {
        const recipientByKey = new Map(
            selectedUsers.map((user) => [user.key, user])
        );

        selectedGroupUserKeys.forEach((userKey) => {
            const matchingUser = users.find((user) => user.key === userKey);
            if (matchingUser) {
                recipientByKey.set(userKey, matchingUser);
            }
        });
        recipientByKey.delete(currentUserData?.key);

        return Array.from(recipientByKey.values());
    }, [currentUserData, selectedGroupUserKeys, selectedUsers, users]);

    const shouldPrepareUserLists = activeTab === 1 && !usersTabLoading;
    const filteredUsers = shouldPrepareUserLists
        ? availableUsers.filter(
              (user) =>
                  !selectedUsers.some(
                      (selected) => selected.key === user.key
                  ) &&
                  (user.fullName || "")
                      .toLowerCase()
                      .includes(usernameFilter.trim().toLowerCase())
          )
        : [];

    const sortedHubs = [...availableHubs].sort(
        (first, second) =>
            (first.ancestors?.length || 0) - (second.ancestors?.length || 0)
    );

    const getHubBreadcrumb = (hub) => {
        if (!hub) return "Hub unavailable";

        const ancestorNames = (hub.ancestors || [])
            .map((ancestorKey) =>
                folders.find((folder) => folder.key === ancestorKey)
            )
            .filter(Boolean)
            .sort(
                (first, second) =>
                    (first.ancestors?.length || 0) -
                    (second.ancestors?.length || 0)
            )
            .map((ancestorHub) => ancestorHub.name);

        return [...ancestorNames, hub.name].join(" › ");
    };

    const selectedGroupOptions = selectedGroups
        .map((group) => ({
            group,
            hub: sortedHubs.find((hub) =>
                hub.groups.some((hubGroup) => hubGroup.key === group.key)
            ),
        }))
        .filter(({ group, hub }) =>
            `${group.name} ${getHubBreadcrumb(hub)}`
                .toLowerCase()
                .includes(selectedGroupFilter.trim().toLowerCase())
        )
        .sort((first, second) => {
            const hubComparison = getHubBreadcrumb(first.hub).localeCompare(
                getHubBreadcrumb(second.hub)
            );
            return (
                hubComparison ||
                first.group.name.localeCompare(second.group.name)
            );
        });

    const filteredAvailableHubs = sortedHubs
        .filter((hub) => hub.key !== PUBLIC_TUTORIALS_HUB_KEY)
        .map((hub) => ({
            ...hub,
            groups: hub.groups
                .filter(
                    (group) =>
                        !selectedGroups.some(
                            (selected) => selected.key === group.key
                        ) &&
                        `${group.displayName || group.name} ${getHubBreadcrumb(hub)}`
                            .toLowerCase()
                            .includes(availableGroupFilter.trim().toLowerCase())
                )
                .sort((first, second) =>
                    (first.displayName || first.name).localeCompare(
                        second.displayName || second.name
                    )
                ),
        }))
        .filter((hub) => hub.groups.length > 0);

    const filteredGroupSelectedUsers = shouldPrepareUserLists
        ? groupCoverage.members.filter((user) =>
              (user.fullName || "")
                  .toLowerCase()
                  .includes(selectedUserFilter.trim().toLowerCase())
          )
        : [];

    const filteredSelectedUsers = shouldPrepareUserLists
        ? directlySelectedUsers.filter((user) =>
              (user.fullName || "")
                  .toLowerCase()
                  .includes(selectedUserFilter.trim().toLowerCase())
          )
        : [];

    const selectedUserSections = [
        {
            key: "individual",
            label: "Added individually",
            icon: <PersonOutlineIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredSelectedUsers,
            viaGroup: false,
        },
        {
            key: "viaGroups",
            label: "Included by a group",
            icon: <GroupsOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredGroupSelectedUsers,
            viaGroup: true,
        },
    ].filter((section) => section.users.length > 0);

    // With only one section on show its header would just repeat the panel title
    const showSelectedUserSectionHeaders = selectedUserSections.length > 1;

    const handleTabChange = (event, value) => {
        if (value === 1 && activeTab !== 1) {
            beginUsersTabLoading();
        }
        setActiveTab(value);
    };

    function handleReviewDeliveryOptions() {
        deliveryOptionsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }

    function handleDeliveryChange(event) {
        const { checked, name } = event.target;

        if (name === "notification") {
            setSendNotifications(checked);
        } else if (name === "email") {
            setSendEmails(checked);
        } else if (name === "embargo") {
            setEmbargoNotifications(checked);
            setFromDate(new Date());
        }

        if (
            (name === "notification" && !checked && !sendEmails) ||
            (name === "email" && !checked && !sendNotifications)
        ) {
            setEmbargoNotifications(false);
            setFromDate(new Date());
        }
    }

    function resetAndClose() {
        if (isSaving) {
            return;
        }
        setUsernameFilter("");
        setSelectedUserFilter("");
        setSelectedGroupFilter("");
        setAvailableGroupFilter("");
        setActiveTab(0);
        setViewingGroup(null);
        setSendNotifications(true);
        setSendEmails(false);
        setEmbargoNotifications(false);
        setSilentShareConfirmOpen(false);
        messageRef.current = "";
        setFromDate(new Date());
        setSaveError("");
        setSelectedGroups([]);
        setSelectedUsers([]);
        props.onClose();
    }

    function selectUser(user) {
        setSelectedUsers((current) => [
            ...current,
            { ...user, canEdit: false },
        ]);
        setUsernameFilter("");
    }

    function selectGroup(group) {
        setSelectedGroups((current) => [
            ...current,
            { ...group, canEdit: false },
        ]);
    }

    function updateUserPermission(userKey) {
        setSelectedUsers((current) =>
            current.map((user) =>
                user.key === userKey
                    ? { ...user, canEdit: !user.canEdit }
                    : user
            )
        );
    }

    function updateGroupPermission(groupKey) {
        setSelectedGroups((current) =>
            current.map((group) =>
                group.key === groupKey
                    ? { ...group, canEdit: !group.canEdit }
                    : group
            )
        );
    }

    function removeGroup(groupToRemove) {
        const remainingGroups = selectedGroups.filter(
            (group) => group.key !== groupToRemove.key
        );
        const remainingGroupUserKeys = new Set(
            remainingGroups.flatMap((group) =>
                (group.users || []).map((user) => user.key)
            )
        );
        const removedGroupUserKeys = new Set(
            (groupToRemove.users || []).map((user) => user.key)
        );

        setSelectedUsers((current) =>
            current.filter(
                (user) =>
                    !removedGroupUserKeys.has(user.key) ||
                    remainingGroupUserKeys.has(user.key)
            )
        );
        setSelectedGroups(remainingGroups);
    }

    // Nobody being told is only a mistake worth interrupting for when there is
    // somebody to tell.
    const wouldShareSilently =
        recipients.length > 0 && !sendNotifications && !sendEmails;

    function handleShareClick() {
        if (wouldShareSilently) {
            setSilentShareConfirmOpen(true);
            return;
        }

        handleShare();
    }

    function handleSilentShareCancel() {
        setSilentShareConfirmOpen(false);
        handleReviewDeliveryOptions();
    }

    function handleSilentShareConfirm() {
        setSilentShareConfirmOpen(false);
        handleShare();
    }

    async function handleShare() {
        const usersObject = {};
        selectedUsers.forEach((user) => {
            usersObject[user.key] = { canEdit: Boolean(user.canEdit) };
        });

        const groupsObject = {};
        selectedGroups.forEach((group) => {
            groupsObject[group.key] = { canEdit: Boolean(group.canEdit) };
        });

        const notificationDate = embargoNotifications ? fromDate : new Date();
        const title = props.projectInfo.title || "Untitled project";
        const tasks = [
            firebase
                .database()
                .ref("projectinfo")
                .child(props.projectInfo.key)
                .update({ users: usersObject, groups: groupsObject }),
        ];

        if (sendNotifications && recipients.length > 0) {
            tasks.push(
                firebase
                    .database()
                    .ref("notifications")
                    .child(props.projectInfo.key)
                    .set({
                        title,
                        type: "project",
                        users: jsonFromArray(recipients),
                        message: messageRef.current,
                        date: dayjs().toString(),
                        fromDate: dayjs(notificationDate).toString(),
                    })
            );
        }

        if (sendEmails) {
            const html = buildSharedItemEmailHtml({
                actionLabel: "View project",
                assetType: "project",
                link:
                    "https://nodiant.faw.cymru/projects/v?key=" +
                    encodeURIComponent(props.projectInfo.key),
                message: messageRef.current,
                title,
            });
            tasks.push(
                sendSharedItemEmails({
                    embargoNotifications,
                    firebase,
                    html,
                    label: `project: ${title}`,
                    notificationDate,
                    recipients,
                    subject: "Nodiant Online - New Project Available",
                })
            );
        }

        setIsSaving(true);
        setSaveError("");
        try {
            await Promise.all(tasks);

            enqueueSnackbar(
                buildShareFeedbackMessage({
                    assetLabel: "Project",
                    userCount: selectedUsers.length,
                    groupCount: selectedGroups.length,
                    sendNotifications,
                    sendEmails,
                    embargoNotifications,
                    notificationDate,
                }),
                {
                    variant: "success",
                    autoHideDuration: 4500,
                }
            );

            messageRef.current = "";
            setIsSaving(false);
            props.onClose(selectedUsers.length, selectedGroups.length);
        } catch (error) {
            console.error("Unable to share project", error);
            setSaveError(
                "The project could not be shared. Please check your connection and try again."
            );
            setIsSaving(false);
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={resetAndClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="share-project-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="share-project-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Share project
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the groups and individual users who can access
                        this project.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close sharing"
                    onClick={resetAndClose}
                    disabled={isSaving}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {isLoading ? (
                    <Box
                        role="status"
                        sx={{
                            minHeight: 360,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress
                            size={32}
                            aria-label="Loading sharing options"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading sharing options…
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "grid", gap: 2 }}>
                        {saveError && (
                            <Alert severity="error">{saveError}</Alert>
                        )}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 1,
                            }}
                        >
                            {[
                                {
                                    icon: <GroupsOutlinedIcon />,
                                    label: "Groups",
                                    value: selectedGroups.length,
                                    // Counted across groups, so anyone in more
                                    // than one selected group is counted once
                                    caption: `Containing ${
                                        groupCoverage.memberKeys.size
                                    } ${
                                        groupCoverage.memberKeys.size === 1
                                            ? "user"
                                            : "users"
                                    }`,
                                },
                                {
                                    icon: <PersonOutlineIcon />,
                                    label: "Individual users",
                                    value: directlySelectedUsers.length,
                                },
                                {
                                    icon: <ShareOutlinedIcon />,
                                    label: "Recipients",
                                    value: recipients.length,
                                },
                            ].map((summary) => (
                                <Box
                                    key={summary.label}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: "primary.main",
                                            display: "grid",
                                        }}
                                    >
                                        {summary.icon}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {summary.label}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{ lineHeight: 1 }}
                                        >
                                            {summary.value}
                                        </Typography>
                                        {summary.caption && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: "block",
                                                    mt: 0.4,
                                                }}
                                            >
                                                {summary.caption}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Paper variant="outlined" sx={surfaceSx}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 800 }}
                                >
                                    People with access
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Everyone listed can view the project. Grant
                                    collaboration access where needed.
                                </Typography>
                            </Box>
                            <Divider />
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                sx={{
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Tab
                                    icon={<GroupsOutlinedIcon />}
                                    iconPosition="start"
                                    label={`Groups (${selectedGroups.length})`}
                                />
                                <Tab
                                    icon={<PersonOutlineIcon />}
                                    iconPosition="start"
                                    label={`Users (${directlySelectedUsers.length})`}
                                />
                            </Tabs>

                            <Box
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "minmax(0, 1fr)",
                                        md: "repeat(2, minmax(0, 1fr))",
                                    },
                                    gap: 2,
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                {activeTab === 0 ? (
                                    <>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Selected groups"
                                                count={selectedGroups.length}
                                            />
                                            <TextField
                                                label="Search selected groups"
                                                size="small"
                                                fullWidth
                                                value={selectedGroupFilter}
                                                onChange={(event) =>
                                                    setSelectedGroupFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            <List
                                                disablePadding
                                                sx={{
                                                    height: { md: 390 },
                                                    maxHeight: {
                                                        xs: 300,
                                                        md: 390,
                                                    },
                                                    overflowY: "auto",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {selectedGroupOptions.length ===
                                                0 ? (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No groups currently have access."
                                                            primaryTypographyProps={{
                                                                color: "text.secondary",
                                                                variant:
                                                                    "body2",
                                                            }}
                                                        />
                                                    </ListItem>
                                                ) : (
                                                    selectedGroupOptions.map(
                                                        (
                                                            { group, hub },
                                                            index
                                                        ) => (
                                                            <React.Fragment
                                                                key={group.key}
                                                            >
                                                                {(index === 0 ||
                                                                    selectedGroupOptions[
                                                                        index -
                                                                            1
                                                                    ].hub
                                                                        ?.key !==
                                                                        hub?.key) && (
                                                                    <ShareDialogGroupHeader
                                                                        breadcrumb={getHubBreadcrumb(
                                                                            hub
                                                                        )}
                                                                    />
                                                                )}
                                                                <AccessRow
                                                                    avatar={
                                                                        <ShareDialogGroupIcon />
                                                                    }
                                                                    title={
                                                                        group.name
                                                                    }
                                                                    secondary={`${getHubBreadcrumb(hub)} · ${
                                                                        (
                                                                            group.users ||
                                                                            []
                                                                        ).length
                                                                    } ${
                                                                        (
                                                                            group.users ||
                                                                            []
                                                                        )
                                                                            .length ===
                                                                        1
                                                                            ? "member"
                                                                            : "members"
                                                                    }`}
                                                                    canEdit={
                                                                        group.canEdit
                                                                    }
                                                                    onViewMembers={handleViewGroupMembers(
                                                                        group,
                                                                        hub
                                                                    )}
                                                                    onChangePermission={() =>
                                                                        updateGroupPermission(
                                                                            group.key
                                                                        )
                                                                    }
                                                                    onRemove={() =>
                                                                        removeGroup(
                                                                            group
                                                                        )
                                                                    }
                                                                />
                                                                {index <
                                                                    selectedGroupOptions.length -
                                                                        1 &&
                                                                    selectedGroupOptions[
                                                                        index +
                                                                            1
                                                                    ].hub
                                                                        ?.key ===
                                                                        hub?.key && (
                                                                        <Divider />
                                                                    )}
                                                            </React.Fragment>
                                                        )
                                                    )
                                                )}
                                            </List>
                                        </Paper>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Available groups"
                                                count={filteredAvailableHubs.reduce(
                                                    (count, hub) =>
                                                        count +
                                                        hub.groups.length,
                                                    0
                                                )}
                                            />
                                            <TextField
                                                label="Search available groups"
                                                size="small"
                                                fullWidth
                                                value={availableGroupFilter}
                                                onChange={(event) =>
                                                    setAvailableGroupFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            <List
                                                disablePadding
                                                sx={{
                                                    height: { md: 390 },
                                                    maxHeight: {
                                                        xs: 300,
                                                        md: 390,
                                                    },
                                                    overflowY: "auto",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {filteredAvailableHubs.length ===
                                                0 ? (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="No matching groups are available."
                                                            primaryTypographyProps={{
                                                                color: "text.secondary",
                                                                variant:
                                                                    "body2",
                                                            }}
                                                        />
                                                    </ListItem>
                                                ) : (
                                                    filteredAvailableHubs.map(
                                                        (hub) => (
                                                            <React.Fragment
                                                                key={hub.key}
                                                            >
                                                                <ShareDialogGroupHeader
                                                                    breadcrumb={getHubBreadcrumb(
                                                                        hub
                                                                    )}
                                                                />
                                                                {hub.groups.map(
                                                                    (
                                                                        group,
                                                                        index
                                                                    ) => (
                                                                        <ShareDialogAvailableGroupRow
                                                                            key={
                                                                                group.key
                                                                            }
                                                                            name={
                                                                                group.displayName ||
                                                                                group.name
                                                                            }
                                                                            memberCount={
                                                                                (
                                                                                    group.users ||
                                                                                    []
                                                                                )
                                                                                    .length
                                                                            }
                                                                            onClick={() =>
                                                                                selectGroup(
                                                                                    group
                                                                                )
                                                                            }
                                                                            divided={
                                                                                index <
                                                                                hub
                                                                                    .groups
                                                                                    .length -
                                                                                    1
                                                                            }
                                                                        />
                                                                    )
                                                                )}
                                                            </React.Fragment>
                                                        )
                                                    )
                                                )}
                                            </List>
                                        </Paper>
                                    </>
                                ) : (
                                    <>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Selected users"
                                                count={
                                                    usersTabLoading
                                                        ? "—"
                                                        : directlySelectedUsers.length
                                                }
                                            />
                                            <TextField
                                                label="Search selected users"
                                                size="small"
                                                fullWidth
                                                value={selectedUserFilter}
                                                disabled={usersTabLoading}
                                                onChange={(event) =>
                                                    setSelectedUserFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            {usersTabLoading ? (
                                                <ShareDialogUserListLoading label="Loading selected users…" />
                                            ) : (
                                                <List
                                                    disablePadding
                                                    sx={{
                                                        height: { md: 390 },
                                                        maxHeight: {
                                                            xs: 300,
                                                            md: 390,
                                                        },
                                                        overflowY: "auto",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    {selectedUserSections.length ===
                                                    0 ? (
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="No users have been added individually."
                                                                primaryTypographyProps={{
                                                                    color: "text.secondary",
                                                                    variant:
                                                                        "body2",
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ) : (
                                                        selectedUserSections.map(
                                                            (section) => (
                                                                <React.Fragment
                                                                    key={
                                                                        section.key
                                                                    }
                                                                >
                                                                    {showSelectedUserSectionHeaders && (
                                                                        <ShareDialogUserSectionHeader
                                                                            icon={
                                                                                section.icon
                                                                            }
                                                                            label={
                                                                                section.label
                                                                            }
                                                                            count={
                                                                                section
                                                                                    .users
                                                                                    .length
                                                                            }
                                                                        />
                                                                    )}
                                                                    {section.users.map(
                                                                        (
                                                                            user,
                                                                            index
                                                                        ) =>
                                                                            section.viaGroup ? (
                                                                                <ShareDialogGroupUserRow
                                                                                    key={
                                                                                        user.key
                                                                                    }
                                                                                    user={
                                                                                        user
                                                                                    }
                                                                                    groupNames={groupCoverage.groupNamesByUserKey.get(
                                                                                        user.key
                                                                                    )}
                                                                                    roomy
                                                                                    divided={
                                                                                        index <
                                                                                        section
                                                                                            .users
                                                                                            .length -
                                                                                            1
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                <React.Fragment
                                                                                    key={
                                                                                        user.key
                                                                                    }
                                                                                >
                                                                    <AccessRow
                                                                        avatar={
                                                                            <UserAvatar
                                                                                userOrHub={
                                                                                    user
                                                                                }
                                                                                path="users"
                                                                                variant="circular"
                                                                                size="medium"
                                                                            />
                                                                        }
                                                                        title={
                                                                            user.fullName
                                                                        }
                                                                        secondary={
                                                                            user.customRoleName ||
                                                                            "Individual access"
                                                                        }
                                                                        canEdit={
                                                                            user.canEdit
                                                                        }
                                                                        onChangePermission={() =>
                                                                            updateUserPermission(
                                                                                user.key
                                                                            )
                                                                        }
                                                                        onRemove={() =>
                                                                            setSelectedUsers(
                                                                                (
                                                                                    current
                                                                                ) =>
                                                                                    current.filter(
                                                                                        (
                                                                                            item
                                                                                        ) =>
                                                                                            item.key !==
                                                                                            user.key
                                                                                    )
                                                                            )
                                                                        }
                                                                    />
                                                                    {index <
                                                                        section
                                                                            .users
                                                                            .length -
                                                                            1 && (
                                                                        <Divider />
                                                                    )}
                                                                                </React.Fragment>
                                                                            )
                                                                    )}
                                                                </React.Fragment>
                                                            )
                                                        )
                                                    )}
                                                </List>
                                            )}
                                        </Paper>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                ...surfaceSx,
                                                p: { xs: 1.5, sm: 2 },
                                            }}
                                        >
                                            <ShareDialogPanelHeading
                                                title="Available users"
                                                count={
                                                    usersTabLoading
                                                        ? "—"
                                                        : filteredUsers.length
                                                }
                                            />
                                            <TextField
                                                label="Search available users"
                                                size="small"
                                                fullWidth
                                                value={usernameFilter}
                                                disabled={usersTabLoading}
                                                onChange={(event) =>
                                                    setUsernameFilter(
                                                        event.target.value
                                                    )
                                                }
                                                sx={shareDialogSearchSx}
                                            />
                                            {usersTabLoading ? (
                                                <ShareDialogUserListLoading label="Loading available users…" />
                                            ) : (
                                                <List
                                                    disablePadding
                                                    sx={{
                                                        height: { md: 390 },
                                                        maxHeight: {
                                                            xs: 300,
                                                            md: 390,
                                                        },
                                                        overflowY: "auto",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    {filteredUsers.length ===
                                                    0 ? (
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="No matching users are available."
                                                                primaryTypographyProps={{
                                                                    color: "text.secondary",
                                                                    variant:
                                                                        "body2",
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ) : (
                                                        filteredUsers.map(
                                                            (user, index) => (
                                                                <React.Fragment
                                                                    key={
                                                                        user.key
                                                                    }
                                                                >
                                                                    <ListItemButton
                                                                        onClick={() =>
                                                                            selectUser(
                                                                                user
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            gap: 1,
                                                                            px: 1.25,
                                                                            py: 0.65,
                                                                        }}
                                                                    >
                                                                        <ListItemAvatar>
                                                                            <UserAvatar
                                                                                userOrHub={
                                                                                    user
                                                                                }
                                                                                path="users"
                                                                                variant="circular"
                                                                                size="small"
                                                                            />
                                                                        </ListItemAvatar>
                                                                        <ListItemText
                                                                            primary={
                                                                                user.fullName
                                                                            }
                                                                            secondary={
                                                                                user.customRoleName
                                                                            }
                                                                            primaryTypographyProps={{
                                                                                variant:
                                                                                    "body2",
                                                                                fontWeight: 800,
                                                                            }}
                                                                        />
                                                                        <AddCircleOutlineIcon
                                                                            fontSize="small"
                                                                            sx={{
                                                                                color: "primary.main",
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                    {index <
                                                                        filteredUsers.length -
                                                                            1 && (
                                                                        <Divider />
                                                                    )}
                                                                </React.Fragment>
                                                            )
                                                        )
                                                    )}
                                                </List>
                                            )}
                                        </Paper>
                                    </>
                                )}
                            </Box>
                        </Paper>

                        <Box ref={deliveryOptionsRef}>
                            <ShareDialogDeliveryOptions
                                assetType="project"
                                sendNotifications={sendNotifications}
                                sendEmails={sendEmails}
                                embargoNotifications={embargoNotifications}
                                message={messageRef.current}
                                messageResetKey={props.open}
                                fromDate={fromDate}
                                onDeliveryChange={handleDeliveryChange}
                                onMessageChange={(value) => {
                                    messageRef.current = value;
                                }}
                                onDateChange={setFromDate}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                {!isLoading && (
                    <ShareDialogDeliverySummary
                        sendNotifications={sendNotifications}
                        sendEmails={sendEmails}
                        embargoNotifications={embargoNotifications}
                        fromDate={fromDate}
                        recipientCount={recipients.length}
                        onReview={handleReviewDeliveryOptions}
                    />
                )}
                <Button
                    color="inherit"
                    onClick={resetAndClose}
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<ShareOutlinedIcon />}
                    onClick={handleShareClick}
                    disabled={isLoading || isSaving}
                >
                    {isSaving ? "Sharing…" : "Share project"}
                </Button>
            </DialogActions>
            {isSaving && <LinearProgress />}

            <ShareDialogGroupMembersDialog
                open={viewingGroup !== null}
                groupName={
                    viewingGroup
                        ? viewingGroup.group.displayName ||
                          viewingGroup.group.name
                        : ""
                }
                breadcrumb={
                    viewingGroup ? getHubBreadcrumb(viewingGroup.hub) : ""
                }
                members={viewedGroupMembers}
                onClose={handleGroupMembersClose}
            />

            <ShareDialogSilentShareConfirm
                open={silentShareConfirmOpen}
                assetType="project"
                recipientCount={recipients.length}
                onCancel={handleSilentShareCancel}
                onConfirm={handleSilentShareConfirm}
            />
        </Dialog>
    );
}
